import { z } from 'zod';
import { isoDatumTijdSchema } from './common';

/** A source column that was recognised, and the listing field it was mapped to. */
export const herkendeKolomSchema = z.object({
  kolom: z.string(),
  veld: z.string(),
});
export type HerkendeKolom = z.infer<typeof herkendeKolomSchema>;

/**
 * Waarom een woning geen punt op de kaart heeft.
 *
 * Het datasets-scherm liet zien hoevéél woningen geen coordinaat hadden, en dat
 * las als evenveel mislukkingen. Dat is het niet: de Funda-scrape mengt bestaande
 * koopwoningen met nieuwbouwprojecten die nog geen adres hebben - "Aletta Heights
 * (Bouwnr. 0208)" is een projectnaam, geen adres, en daar hoort geen punt bij.
 * Die groep uit elkaar houden is het verschil tussen "er ging iets mis" en "dit
 * klopt zo".
 */
export const ZONDER_COORDINAAT_REDENEN = ['nieuwbouw', 'geen_postcode', 'niet_gevonden'] as const;
export const zonderCoordinaatRedenSchema = z.enum(ZONDER_COORDINAAT_REDENEN);
export type ZonderCoordinaatReden = z.infer<typeof zonderCoordinaatRedenSchema>;

/** Korte Nederlandse tekst per reden, zoals hij op het scherm hoort te staan. */
export const ZONDER_COORDINAAT_REDEN_LABELS: Record<ZonderCoordinaatReden, string> = {
  nieuwbouw: 'nieuwbouw zonder adres',
  geen_postcode: 'geen postcode',
  niet_gevonden: 'adres niet gevonden',
};

export const zonderCoordinaatTellingSchema = z.object({
  reden: zonderCoordinaatRedenSchema,
  aantal: z.number().int().positive(),
});
export type ZonderCoordinaatTelling = z.infer<typeof zonderCoordinaatTellingSchema>;

/**
 * Hoe het met de foto's staat: hoeveel er op schijf zijn, hoeveel de fotohaler
 * nog moet doen, en hoeveel na drie pogingen zijn opgegeven. `bytes` is wat er
 * op schijf staat, voor het budget.
 */
export const fotoTellingSchema = z.object({
  bewaard: z.number().int().nonnegative(),
  teHalen: z.number().int().nonnegative(),
  mislukt: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
});
export type FotoTelling = z.infer<typeof fotoTellingSchema>;

/** GET /fotos/stand - de telling over alles, plus het budget en of de haler loopt. */
export const fotoStandSchema = fotoTellingSchema.extend({
  budgetBytes: z.number().int().nonnegative(),
  /** Of de fotohaler aan staat (FOTO_HAAL) en nog binnen het budget zit. */
  actief: z.boolean(),
  gestopt: z.boolean(),
});
export type FotoStand = z.infer<typeof fotoStandSchema>;

/** Wat een handeling over een groep woningen oplevert: hoeveel rijen er geraakt zijn. */
export const bulkUitslagSchema = z.object({
  aantal: z.number().int().nonnegative(),
});
export type BulkUitslag = z.infer<typeof bulkUitslagSchema>;

/**
 * DELETE /datasets/:id - wat er weg is en wat bleef. `elders` zijn woningen uit
 * dit bestand die intussen bij een andere dataset horen (een feed die ze later
 * zag) en daarom blijven staan.
 */
export const datasetVerwijderdSchema = z.object({
  woningen: z.number().int().nonnegative(),
  elders: z.number().int().nonnegative(),
  bestandsnaam: z.string(),
});
export type DatasetVerwijderd = z.infer<typeof datasetVerwijderdSchema>;

/**
 * GET /woningen/:id/fotos - welke foto's een woning heeft. Het popupje bladert
 * erdoor met `/woningen/:id/fotos/:volgnummer`; `bewaard` zegt of dat van schijf
 * komt of eerst nog bij het CDN gehaald wordt.
 */
export const fotoLijstSchema = z.object({
  aantal: z.number().int().nonnegative(),
  fotos: z.array(
    z.object({
      volgnummer: z.number().int().nonnegative(),
      bewaard: z.boolean(),
    }),
  ),
});
export type FotoLijst = z.infer<typeof fotoLijstSchema>;

/**
 * One import: which file came in, when, how many rows it held and how much of
 * that survived geocoding and had an energy label.
 *
 * The row counts describe the file and are recorded at import time. The listing
 * counts are counted when the screen asks, because a listing belongs to the last
 * import that touched it: a second scrape under a new file name takes them over,
 * and a stored number would keep claiming houses that moved.
 */
export const datasetSchema = z.object({
  id: z.uuid(),
  bron: z.string().min(1),
  bestandsnaam: z.string().min(1),
  geimporteerdOp: isoDatumTijdSchema,
  /** Rows read from the file - duplicates and skipped rows included. */
  aantalRijen: z.number().int().nonnegative(),
  /** Listings that belong to this dataset right now. */
  aantalWoningen: z.number().int().nonnegative(),
  /** Rows repeating a (bron, bron-id) already seen in the same file. */
  aantalDubbel: z.number().int().nonnegative(),
  /** Rows without an asking price or address. */
  aantalOvergeslagen: z.number().int().nonnegative(),
  aantalGegeocodeerd: z.number().int().nonnegative(),
  /** Woningen van deze dataset zonder coordinaat: wel in de tabel, niet op de kaart. */
  aantalZonderCoordinaat: z.number().int().nonnegative(),
  /** En waarom, per reden, aflopend. Leeg wanneer alles een punt heeft. */
  zonderCoordinaatRedenen: z.array(zonderCoordinaatTellingSchema),
  aantalMetLabel: z.number().int().nonnegative(),
  /**
   * De foto's van de woningen in deze dataset: op schijf, nog te halen, of
   * opgegeven. Geteld op het moment van vragen, net als de woningen.
   */
  fotos: fotoTellingSchema.default({ bewaard: 0, teHalen: 0, mislukt: 0, bytes: 0 }),
  herkendeKolommen: z.array(herkendeKolomSchema),
  nietHerkendeKolommen: z.array(z.string()),
});
export type Dataset = z.infer<typeof datasetSchema>;
